import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

// Styled components
const Container = styled.div`
  display: flex;
  min-height: 100vh;
  padding: 0;
  margin: 0;
  background: #f8f9fa;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const LeftSection = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 4rem;
  background: linear-gradient(135deg, #6e8efb, #a777e3);
  color: white;
  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const RightSection = styled(motion.div)`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: white;
`;

const FormContainer = styled(motion.div)`
  width: 100%;
  max-width: 500px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  padding: 2.5rem;
`;

const Title = styled(motion.h2)`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  font-weight: 700;
  background: linear-gradient(to right, #6e8efb, #a777e3);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled(motion.p)`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  color: #666;
  line-height: 1.6;
`;

const Input = styled(motion.input)`
  width: 100%;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  &:focus {
    border-color: #a777e3;
    box-shadow: 0 0 0 3px rgba(167, 119, 227, 0.2);
    outline: none;
  }
`;

const TextArea = styled(motion.textarea)`
  width: 100%;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border: 2px solid #eee;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 120px;
  transition: all 0.3s ease;
  &:focus {
    border-color: #a777e3;
    box-shadow: 0 0 0 3px rgba(167, 119, 227, 0.2);
    outline: none;
  }
`;

const Button = styled(motion.button)`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(to right, #6e8efb, #a777e3);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(167, 119, 227, 0.4);
  }
`;

const Image = styled(motion.img)`
  max-width: 100%;
  height: auto;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5
    }
  }
};

const FranchiseApply = () => {
  return (
    <Container>
      <LeftSection
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <motion.div
          initial={{ x: -50, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <h1 style={{ fontSize: '3rem', marginBottom: '1rem' }}>Join Our Franchise Network</h1>
          <p style={{ fontSize: '1.2rem', lineHeight: '1.6', marginBottom: '2rem' }}>
            Become part of our successful franchise family and start your entrepreneurial journey with our proven business model.
          </p>
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <Image 
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-1.2.1&auto=format&fit=crop&w=1000&q=80" 
              alt="Franchise Business"
              whileHover={{ scale: 1.02 }}
            />
          </motion.div>
        </motion.div>
      </LeftSection>

      <RightSection>
        <FormContainer
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          whileHover={{ y: -5 }}
        >
          <Title
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Apply Now
          </Title>
          <Subtitle
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            Fill out the form below and our team will get back to you within 24 hours.
          </Subtitle>

          <motion.form
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <Input
              type="text"
              placeholder="Full Name"
              variants={itemVariants}
              whileFocus={{ scale: 1.02 }}
            />
            <Input
              type="email"
              placeholder="Email Address"
              variants={itemVariants}
              whileFocus={{ scale: 1.02 }}
            />
            <Input
              type="tel"
              placeholder="Phone Number"
              variants={itemVariants}
              whileFocus={{ scale: 1.02 }}
            />
            <Input
              type="text"
              placeholder="Location/City"
              variants={itemVariants}
              whileFocus={{ scale: 1.02 }}
            />
            <TextArea
              placeholder="Tell us about your business experience and why you're interested in this franchise..."
              variants={itemVariants}
              whileFocus={{ scale: 1.02 }}
            />
            <Button
              type="submit"
              variants={itemVariants}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Submit Application
            </Button>
          </motion.form>
        </FormContainer>
      </RightSection>
    </Container>
  );
};

export default FranchiseApply;